import { AxiosResponse } from 'axios'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { Column } from 'common/components/BasicTable/types'
import { DateRange } from 'common/components/DateRangePicker'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { useMutateDeleteFoodByProduct } from 'modules/food/api/mutation'
import {
  useGetFoodLineChart,
  useGetFoodToday,
  useGetMeals,
  useGetMealsByProduct,
  useGetRecentFoodByProduct,
} from 'modules/food/api/queries'
import { useFoodsByRange } from 'modules/food/api/selector'
import { MealDetail } from 'modules/food/api/types'
import { selectedFoodIdState, selectedMealIdState } from 'modules/food/stores/atom'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useSpeciesName } from 'modules/pond/api/selector'
import { normalizeSpeciesName } from 'utils/analytics'
import MoreAction from 'modules/pond/components/MoreAction'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useRecoilState, useSetRecoilState } from 'recoil'

import { Box, Stack, Typography, useTheme } from '@mui/material'

import { Icon } from '@iconify/react'

import MealChip from '../MealChip'
import RecordFoodDialog from '../RecordFoodDialog'
import './style.css'

const FishFoodTable = () => {
  const { t } = useTranslation(['common', 'food'])
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  })

  const {
    data: foods,
    isFetching: isFoodsFetching,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useFoodsByRange({
    startDate: dateRange.startDate ?? undefined,
    endDate: dateRange.endDate ?? undefined,
  })
  const { refetch: refetchChart } = useGetFoodLineChart()
  const { refetch: refetchMeals } = useGetMeals()
  const { refetch: refetchMealByProduct } = useGetMealsByProduct()
  const { refetch: refetchToday } = useGetFoodToday()
  const { refetch: refetchRecentFoodByProduct } = useGetRecentFoodByProduct()

  const theme = useTheme()

  const [open, setOpen] = useState(false)

  const setMealId = useSetRecoilState(selectedMealIdState)
  const [foodId, setFoodId] = useRecoilState(selectedFoodIdState)

  const deleteFoodMutation = useMutateDeleteFoodByProduct()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const onSuccess = (res: AxiosResponse<any>) => {
    const { status } = res.data
    if (status === 200) {
      setTimeout(() => {
        refetch()
        refetchChart()
        refetchMeals()
        refetchToday()
        refetchMealByProduct()
        refetchRecentFoodByProduct()
      }, 1000)
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(
        AnalyticsEvents.feedRecordDeleted,
        normalizedSpecies ? { species: normalizedSpecies } : undefined
      )
    }
  }

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteFoodMutation.mutateAsync(foodId || 0, { onSuccess })
    },
  })

  const getMealRow = (meal: MealDetail) => {
    if (!meal) {
      return { meals: '-' }
    }
    return { meals: <MealChip meal={meal} number={meal.meal.toString()} isDetail={false} /> }
  }

  const groupColumns = [
    {
      row: 1,
      columns: [
        {
          id: 'date',
          title: t('common:date'),
          width: '150px',
        },
        {
          id: 'age',
          title: <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />,
          width: '100px',
        },
        {
          id: 'meals',
          title: <Trans i18nKey={'food:meal.table.header'} />,
          width: '100px',
        },
        {
          id: '',
          title: (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" gap={1}>
              <Icon icon="ic:round-medication-liquid" width="16px" height="16px" />
              <Typography variant="h6" fontWeight={600}>
                {t('food:food.pellet')}
              </Typography>
            </Stack>
          ),
          colSpan: 3,
          width: `300px`,
        },
        {
          id: '',
          title: (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" gap={1}>
              <Icon icon="ic:baseline-energy-savings-leaf" width="16px" height="16px" />
              <Typography variant="h6" fontWeight={600}>
                {t('food:food.natural')}
              </Typography>
            </Stack>
          ),
          colSpan: 2,
          width: `300px`,
        },
        {
          id: '',
          colSpan: 1,
          width: `100px`,
        },
      ],
    },
  ]

  const columns: Column[] = [
    {
      id: 'date',
      format: (val: string) => (val === ' ' ? ' ' : formatThaiShortDateTwoDigitYear(val)),
      width: '100px',
    },
    {
      id: 'age',
      width: '100px',
      format: (val: number) =>
        val.toString() === ' ' ? ' ' : fNumber(val, { disabledDecimal: true }),
    },
    {
      id: 'meals',
      width: '100px',
    },
    {
      id: 'totalRemain',
      title: t('food:total.remain.food.kg'),
      width: '100px',
    },
    {
      id: 'totalFood',
      title: t('food:total.food.kg'),
      format: fNumber,
      width: '100px',
    },
    {
      id: 'accFood',
      title: t('food:acc.food.kg'),
      width: '100px',
    },
    {
      id: 'totalNaturalFeed',
      title: t('food:total.food.kg'),
      format: fNumber,
      width: '100px',
    },
    {
      id: 'naturalAccumulatedFeed',
      title: t('food:acc.food.kg'),
      width: '100px',
    },
    {
      id: 'action',
      title: '',
      width: '100px',
    },
  ]

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpenEdit = (mealId: number, foodId: number) => {
    setMealId(mealId)
    setFoodId(foodId)
    setOpen(true)
  }

  const handleOpenDeleteConfirm = (id: number) => {
    setFoodId(id)
    openConfirmDialog()
  }

  const createTableRows = (
    date: string,
    age: number | string,
    meals: MealDetail[],
    accFood: number,
    naturalAccumulatedFeed: number,
    action: {
      mealId?: number
      foodId: number
    },
    index: number,
    mealLen: number
  ) => ({
    date: index === 0 ? date : ' ',
    age: index === 0 ? age : ' ',
    ...getMealRow(meals[index]),
    totalRemain: (
      <Typography color={meals[index]?.remainingFood > 0 ? theme.palette.error.main : 'black'}>
        {meals[index]?.remainingFood ? fNumber(meals[index]?.remainingFood) : '-'}
      </Typography>
    ),
    totalFood: meals[index]?.amount || 0,
    accFood:
      index === 0 ? (
        <Typography fontWeight={theme.typography.fontWeightBold}>{fNumber(accFood)}</Typography>
      ) : (
        ' '
      ),
    totalNaturalFeed: meals[index]?.naturalFeedAmount || 0,
    naturalAccumulatedFeed:
      index === 0 ? (
        <Typography fontWeight={theme.typography.fontWeightBold}>
          {fNumber(naturalAccumulatedFeed)}
        </Typography>
      ) : (
        ' '
      ),
    action:
      index === 0 ? (
        <MoreAction
          featureKey={FeatureKey.Food}
          isHideOnCompleted={false}
          onClickEdit={() => action.mealId && handleOpenEdit(action.mealId, action.foodId)}
          onClickDelete={() => handleOpenDeleteConfirm(action.foodId)}
        />
      ) : null,
    mealLen,
    mealIndex: index,
  })

  const filteredFoods = foods?.filter((item) => {
    if (!dateRange.startDate || !dateRange.endDate) return true

    const d = new Date(item.date)
    return d >= dateRange.startDate && d <= dateRange.endDate
  })

  const rows = filteredFoods
    ?.map((row) => {
      const tableRows = []

      for (let i = 0; i < (row.meals?.length || 0); i++) {
        tableRows.push(
          createTableRows(
            row.date,
            row.shrimpAge,
            row.meals || [],
            row.accumulatedFood,
            row.naturalAccumulatedFeed,
            {
              foodId: row.id,
              mealId: row.meals?.[row.meals.length - 1]?.id,
            },
            i,
            row.meals.length
          )
        )
      }
      return tableRows
    })
    .flat()

  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  const onStyleCell = (row: any) => {
    return {
      borderBottom:
        row.mealIndex === row.mealLen - 1 ? `1px solid ${theme.palette.grey[300]}` : 'none',
    }
  }

  return (
    <>
      <Box className="fish-food-table-container">
        {dialog}
        <BaseTableCard
          title={t('food:feeding.recap.per.day')}
          refresh={refetch}
          isLoading={isFoodsFetching && foods.length === 0}
          isEmpty={foods.length === 0 && !hasNextPage}
          isDateRangePicker={true}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          displayValue={dashboardDateDisplayValue}
        >
          <BasicTable
            groupColumns={groupColumns}
            columns={columns || []}
            rows={rows || []}
            onLoading={onLoadMoreRows}
            isLoading={isFoodsFetching}
            onStyleCell={onStyleCell}
          />
        </BaseTableCard>
        <RecordFoodDialog open={open} onClose={handleClose} isEdit />
      </Box>
    </>
  )
}

export default FishFoodTable
